import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useMutateDeleteRole } from 'modules/member/api/mutation'
import { useRoles } from 'modules/member/api/selector'
import { MemberResponse } from 'modules/member/api/types'
import AccessDialog from 'modules/member/components/AccessDialog'
import { RoleType } from 'modules/member/constants'
import MoreAction from 'modules/pond/components/MoreAction'
import { useUserInfo } from 'modules/user/api/queries'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import Page from 'components/Page'

import './style.css'

const AccessPage = () => {
  const { t } = useTranslation(['member', 'pond'])
  const [open, setOpen] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedRole, setSelectedRole] = useState<MemberResponse>()

  const { data: roles, isFetching: isRoleFetching } = useRoles()
  const deleteRoleMutation = useMutateDeleteRole()
  const { data: userInfo } = useUserInfo()

  const { dialog, openDialog: openConfirmDeleteDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (userInfo && selectedRole) {
        await deleteRoleMutation.mutateAsync({
          userId: userInfo.id,
          roleId: selectedRole.id,
        })
      }
    },
  })

  const handleClose = () => {
    setIsEdit(false)
    setOpen(false)
  }

  return (
    <Page title={t('access.title')}>
      {dialog}

      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '77vh',
          bgcolor: 'common.white',
          borderRadius: '24px',
          p: 5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: 'common.black',
            fontWeight: 600,
            fontSize: '18px',
            mb: 2,
          }}
        >
          {t('member:manage.role')}
        </Typography>

        {isRoleFetching ? (
          <Typography align="center" sx={{ py: 5 }}>
            {t('common:loading')}
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table
              stickyHeader
              aria-label="access table"
              sx={{
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& thead th': {
                  fontSize: '16px',
                  color: 'text.primary',
                  backgroundColor: '#F0F0F0',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& tbody td': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& thead th:first-of-type': { borderTopLeftRadius: 12 },
                '& thead th:last-of-type': { borderTopRightRadius: 12 },
                '& tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: 12 },
                '& tbody tr:last-of-type td:last-of-type': { borderBottomRightRadius: 12 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: 16, width: '100px' }}>
                    {t('table.position')}
                  </TableCell>
                  <TableCell sx={{ width: '100px' }} />
                </TableRow>
              </TableHead>

              <TableBody>
                {roles?.map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 400, fontSize: 16 }}>{role.name}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {role.role_type === RoleType.Custom && (
                        <MoreAction
                          featureKey={FeatureKey.User}
                          onClickOptions={[
                            {
                              icon: <EditIcon />,
                              text: t('pond:edit'),
                              action: () => {
                                setSelectedRole({ id: role.id, name: role.name })
                                setIsEdit(true)
                                setOpen(true)
                              },
                              crudIdx: CRUDAction.Update,
                            },
                            {
                              icon: <DeleteIcon />,
                              text: t('pond:delete'),
                              action: () => {
                                setSelectedRole({ id: role.id, name: role.name })
                                openConfirmDeleteDialog()
                              },
                              crudIdx: CRUDAction.Delete,
                            },
                          ]}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {open && (
        <AccessDialog
          open={open}
          onClose={handleClose}
          isEdit={isEdit}
          initialRole={selectedRole}
        />
      )}
    </Page>
  )
}

export default AccessPage
